<template>
  <div
    class="modal-overlay registration-modal-overlay registration-modal-overlay--standalone"
    @mousedown.self="onOverlayMouseDown"
    @mouseup.self="onOverlayMouseUp"
  >
    <div class="modal-container registration-modal-container" role="dialog" aria-modal="true" aria-label="Sign up">
      <div class="modal-header">
        <h3>Sign up</h3>
        <button
          type="button"
          class="modal-close-button"
          aria-label="Close sign up form"
          @click="close"
        >
          ×
        </button>
      </div>

      <div class="modal-body registration-modal-body">
        <form class="details-form" @submit.prevent="handleSubmit">
          <div class="form-row">
            <div class="form-field">
              <label for="signup-first-name" class="field-label field-label--compact">First Name</label>
              <input
                id="signup-first-name"
                type="text"
                autocomplete="given-name"
                enterkeyhint="next"
                v-model="firstName"
                placeholder="Enter your first name"
                required
              />
            </div>
            <div class="form-field">
              <label for="signup-surname" class="field-label field-label--compact">Surname</label>
              <input
                id="signup-surname"
                type="text"
                autocomplete="family-name"
                enterkeyhint="next"
                v-model="surname"
                placeholder="Enter your surname"
                required
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-field">
              <label for="signup-email" class="field-label field-label--compact">Email</label>
              <input
                id="signup-email"
                type="email"
                autocomplete="email"
                enterkeyhint="next"
                v-model="email"
                placeholder="you@example.com"
                required
              />
            </div>

            <div class="form-field phone-field">
              <label for="signup-mobile-country" class="field-label field-label--compact">Mobile Number</label>
              <div class="phone-inputs">
                <div class="country-select-wrapper">
                  <select
                    id="signup-mobile-country"
                    class="country-select"
                    v-model="selectedCountryIso"
                    autocomplete="tel-country-code"
                    aria-label="Country"
                  >
                    <option disabled value="">Country code</option>
                    <option
                      v-for="country in countryOptions"
                      :key="country.iso"
                      :value="country.iso"
                    >
                      {{ country.name }}
                    </option>
                  </select>
                </div>
                <input
                  id="signup-mobile-number"
                  type="tel"
                  autocomplete="tel-national"
                  inputmode="numeric"
                  enterkeyhint="next"
                  v-model="mobileNumber"
                  @blur="handlePhoneNumberBlur"
                  maxlength="20"
                  placeholder="Mobile"
                  required
                />
                <small v-if="phoneNumberError" class="field-error">{{ phoneNumberError }}</small>
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-field">
              <label for="signup-password" class="field-label field-label--compact">Password</label>
              <div class="password-input">
                <input
                  id="signup-password"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  autocomplete="new-password"
                  enterkeyhint="next"
                  v-model="password"
                  placeholder="At least 8 chars, upper, lower, number"
                  :minlength="PASSWORD_MIN_LENGTH"
                  required
                />
                <span
                  class="toggle-visibility-btn"
                  role="button"
                  tabindex="0"
                  @click="isPasswordVisible = !isPasswordVisible"
                  @keydown.enter.prevent="isPasswordVisible = !isPasswordVisible"
                  @keydown.space.prevent="isPasswordVisible = !isPasswordVisible"
                  :aria-label="isPasswordVisible ? 'Hide password' : 'Show password'"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true" class="toggle-visibility-icon">
                    <path
                      d="M12 5.8c5.2 0 9.4 3.7 11 6.2a1.6 1.6 0 0 1 0 1.8c-1.6 2.5-5.8 6.2-11 6.2s-9.4-3.7-11-6.2a1.6 1.6 0 0 1 0-1.8c1.6-2.5 5.8-6.2 11-6.2Zm0 2c-3.9 0-7.3 2.6-8.8 4.6 1.5 2 4.9 4.6 8.8 4.6s7.3-2.6 8.8-4.6c-1.5-2-4.9-4.6-8.8-4.6Zm0 1.7a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Zm0 2a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z"
                    />
                  </svg>
                </span>
              </div>
              <small class="hint">Min 8 chars, include upper, lower, and a number.</small>
            </div>
            <div class="form-field">
              <label for="signup-confirm-password" class="field-label field-label--compact">Confirm Password</label>
              <div class="password-input">
                <input
                  id="signup-confirm-password"
                  :type="isConfirmPasswordVisible ? 'text' : 'password'"
                  autocomplete="new-password"
                  enterkeyhint="done"
                  v-model="confirmPassword"
                  placeholder="Re-enter your password"
                  :minlength="PASSWORD_MIN_LENGTH"
                  required
                />
                <span
                  class="toggle-visibility-btn"
                  role="button"
                  tabindex="0"
                  @click="isConfirmPasswordVisible = !isConfirmPasswordVisible"
                  @keydown.enter.prevent="isConfirmPasswordVisible = !isConfirmPasswordVisible"
                  @keydown.space.prevent="isConfirmPasswordVisible = !isConfirmPasswordVisible"
                  :aria-label="isConfirmPasswordVisible ? 'Hide confirm password' : 'Show confirm password'"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true" class="toggle-visibility-icon">
                    <path
                      d="M12 5.8c5.2 0 9.4 3.7 11 6.2a1.6 1.6 0 0 1 0 1.8c-1.6 2.5-5.8 6.2-11 6.2s-9.4-3.7-11-6.2a1.6 1.6 0 0 1 0-1.8c1.6-2.5 5.8-6.2 11-6.2Zm0 2c-3.9 0-7.3 2.6-8.8 4.6 1.5 2 4.9 4.6 8.8 4.6s7.3-2.6 8.8-4.6c-1.5-2-4.9-4.6-8.8-4.6Zm0 1.7a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Zm0 2a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z"
                    />
                  </svg>
                </span>
              </div>
            </div>
          </div>

          <div class="consent-row">
            <div class="checkbox-field">
              <input
                id="signup-terms"
                type="checkbox"
                v-model="consent"
                required
              />
              <label for="signup-terms">I consent to the Terms and Conditions</label>
            </div>
            <div class="checkbox-field">
              <input
                id="signup-marketing"
                type="checkbox"
                v-model="marketingConsent"
              />
              <label for="signup-marketing">I consent to marketing emails</label>
            </div>
          </div>

          <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
        </form>
      </div>

      <div class="modal-footer registration-modal-footer">
        <button type="button" class="secondary-btn" @click="close" :disabled="authStore.isLoading">Cancel</button>
        <button
          type="button"
          class="primary-btn"
          @click="handleSubmit"
          :disabled="!canSubmit || authStore.isLoading"
        >
          <span v-if="authStore.isLoading">Creating account…</span>
          <span v-else>Create Account</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js'
import { useAuthStore } from '@/store/auth.store'
import {
  COUNTRY_DIAL_CODES,
  getCountryByDialCode,
  getCountryByIso
} from '@/utils/countryDialCodes'

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'authenticated'): void
  (e: 'verification-required', payload: { email: string; password: string }): void
}>()

const authStore = useAuthStore()
const PASSWORD_MIN_LENGTH = 8
const SOUTH_AFRICA_ISO = 'ZA'
const SOUTH_AFRICA_DIAL_CODE = '+27'

const firstName = ref('')
const surname = ref('')
const email = ref('')
const password = ref('')
const confirmPassword = ref('')
const mobileCountryCode = ref(SOUTH_AFRICA_DIAL_CODE)
const mobileNumber = ref('')
const consent = ref(false)
const marketingConsent = ref(false)
const isPasswordVisible = ref(false)
const isConfirmPasswordVisible = ref(false)
const phoneNumberError = ref('')
const errorMessage = ref('')

const countryOptions = COUNTRY_DIAL_CODES

const selectedCountry = computed(() => {
  return getCountryByDialCode(mobileCountryCode.value) ?? getCountryByIso(SOUTH_AFRICA_ISO)
})

const selectedCountryIso = computed({
  get: () => selectedCountry.value?.iso ?? SOUTH_AFRICA_ISO,
  set: (iso: string) => {
    const next = getCountryByIso(iso) ?? getCountryByIso(SOUTH_AFRICA_ISO)
    if (next) mobileCountryCode.value = next.dialCode
  }
})

const handlePhoneNumberBlur = () => {
  const trimmed = mobileNumber.value.trim()
  if (!trimmed) {
    phoneNumberError.value = ''
    return
  }
  const iso = selectedCountry.value?.iso as CountryCode | undefined
  const parsed = parsePhoneNumberFromString(trimmed, iso)
  if (parsed && parsed.isValid()) {
    mobileNumber.value = parsed.nationalNumber
    if (parsed.country && parsed.country !== selectedCountry.value?.iso) {
      const matched = getCountryByIso(parsed.country)
      if (matched) mobileCountryCode.value = matched.dialCode
    }
    phoneNumberError.value = ''
    return
  }
  const digits = trimmed.replace(/\D/g, '').replace(/^0+/, '')
  mobileNumber.value = digits.slice(0, 15)
  phoneNumberError.value = digits.length >= 7 ? '' : 'Please enter a valid phone number'
}

const canSubmit = computed(() => {
  return (
    firstName.value.trim().length > 0 &&
    surname.value.trim().length > 0 &&
    email.value.trim().length > 0 &&
    password.value.length >= PASSWORD_MIN_LENGTH &&
    password.value === confirmPassword.value &&
    mobileNumber.value.trim().length > 0 &&
    consent.value &&
    !phoneNumberError.value
  )
})

const overlayMouseDownFired = ref(false)
const onOverlayMouseDown = () => { overlayMouseDownFired.value = true }
const onOverlayMouseUp = () => {
  if (overlayMouseDownFired.value) emit('close')
  overlayMouseDownFired.value = false
}
const resetOverlayFlag = () => { overlayMouseDownFired.value = false }

onMounted(() => { document.addEventListener('mouseup', resetOverlayFlag) })
onUnmounted(() => { document.removeEventListener('mouseup', resetOverlayFlag) })

const close = () => emit('close')

const handleSubmit = async () => {
  errorMessage.value = ''
  authStore.error = null

  if (password.value !== confirmPassword.value) {
    errorMessage.value = 'Passwords do not match.'
    return
  }
  if (!canSubmit.value) {
    errorMessage.value = 'Please complete all required fields.'
    return
  }

  const trimmedEmail = email.value.trim()
  const e164Phone = (() => {
    const dial = mobileCountryCode.value.startsWith('+') ? mobileCountryCode.value : `+${mobileCountryCode.value}`
    const local = mobileNumber.value.replace(/\D/g, '').replace(/^0+/, '')
    return `${dial}${local}`
  })()
  const preferredUsername = `${firstName.value.trim()} ${surname.value.trim()}`.trim()

  try {
    const result = await authStore.register(trimmedEmail, password.value, preferredUsername, e164Phone)

    if (result.isSignUpComplete) {
      try {
        await authStore.login(trimmedEmail, password.value)
        if (authStore.isEmailVerified) {
          emit('authenticated')
          return
        }
      } catch (loginErr) {
        console.error('Auto-login after signup failed', loginErr)
      }
      emit('verification-required', { email: trimmedEmail, password: password.value })
      return
    }

    if (result.nextStep?.signUpStep === 'CONFIRM_SIGN_UP') {
      emit('verification-required', { email: trimmedEmail, password: password.value })
      return
    }

    emit('verification-required', { email: trimmedEmail, password: password.value })
  } catch (err: unknown) {
    const e = err as Error
    errorMessage.value = e?.message || 'Sign up failed. Please try again.'
  }
}
</script>

<style scoped>
.registration-modal-overlay--standalone {
  z-index: calc(var(--base-layout-modal-z-index) + 30);
}

.registration-modal-container {
  width: min(760px, 100%);
  max-width: min(760px, 100%);
  max-height: min(88vh, 900px);
  display: flex;
  flex-direction: column;
}

.registration-modal-body {
  overflow-y: auto;
  flex: 1;
  display: flex;
  justify-content: center;
  padding: 1.5rem clamp(10px, 2.4vw, 24px);
}

.registration-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 1.25rem 1.5rem;
}

.details-form {
  width: 100%;
  max-width: 680px;
  margin-inline: auto;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(12px, 1.6vw, 20px);
  margin-bottom: clamp(14px, 1.8vw, 22px);
}

.form-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.form-field label {
  font-weight: var(--base-layout-form-label-weight);
  color: var(--color-heading);
  margin-bottom: var(--base-layout-form-label-margin-bottom);
  font-size: var(--base-layout-form-label-size);
}

.field-label--compact {
  display: block;
}

.form-field input,
.form-field select {
  padding: var(--base-layout-form-input-padding);
  border: var(--base-layout-form-input-border-width) solid var(--color-border);
  border-radius: var(--base-layout-form-input-border-radius);
  background-color: var(--color-background-mute);
  color: var(--color-text);
  font-size: var(--base-layout-form-input-font-size);
  text-align: center;
  transition: all var(--base-layout-transition-normal);
}

.form-field input::placeholder {
  text-align: center;
}

.form-field input:focus,
.form-field select:focus {
  outline: none;
  border-color: var(--vt-c-accent);
  box-shadow: 0 0 0 var(--base-layout-form-input-focus-ring-size) var(--base-layout-form-input-focus-ring-color);
  background-color: var(--color-background-secondary);
}

.phone-field .phone-inputs {
  display: flex;
  flex-direction: column;
  gap: var(--base-layout-form-phone-input-gap);
  align-items: stretch;
  min-width: 0;
  width: 100%;
}

.phone-field .country-select {
  appearance: none;
  text-align: center;
  text-align-last: center;
  color: var(--color-text);
  background-color: var(--color-background-mute);
}

.password-input {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.password-input input {
  width: 100%;
  padding-right: 2.75rem;
}

.toggle-visibility-btn {
  position: absolute;
  right: 0.75rem;
  background: none;
  border: none;
  color: var(--color-text);
  opacity: 0.7;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.toggle-visibility-btn:hover { opacity: 1; }

.toggle-visibility-icon {
  width: 1.1rem;
  height: 1.1rem;
  fill: currentColor;
}

.hint {
  color: var(--color-text);
  opacity: 0.7;
  font-size: 0.8rem;
  margin-top: 6px;
  display: block;
}

.consent-row {
  display: flex;
  flex-direction: column;
  gap: var(--base-layout-consent-row-gap);
  margin: clamp(10px, 1.5vw, 18px) 0 0;
}

.checkbox-field {
  display: flex;
  align-items: center;
  gap: var(--base-layout-checkbox-field-gap);
}

.checkbox-field input[type='checkbox'] {
  width: var(--base-layout-form-checkbox-size);
  height: var(--base-layout-form-checkbox-size);
  min-width: var(--base-layout-form-checkbox-size);
  min-height: var(--base-layout-form-checkbox-size);
  margin: 0;
  flex-shrink: 0;
  cursor: pointer;
  accent-color: var(--vt-c-accent);
}

.checkbox-field label {
  margin: 0;
  font-weight: normal;
  color: var(--color-text);
  font-size: 0.95rem;
}

.field-error {
  color: #fa9696;
  font-size: 0.8rem;
  margin-top: 4px;
}

.error-message {
  color: #fa9696;
  margin-top: 0.75rem;
  font-size: 0.9rem;
  background: rgba(250, 150, 150, 0.08);
  padding: 0.75rem;
  border-radius: 6px;
  text-align: center;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
  .registration-modal-container {
    max-height: min(var(--mobile-modal-max-height), 92vh);
  }
}
</style>
